import { Injectable, Injector } from '@angular/core';
import { forkJoin, from, Observable, of } from 'rxjs';
import { PlanningService } from '@obo-main/services/plannings/planning.service';
import { BuildingService } from '@obo-dashboard/services/building.service';
import { AuthService } from '@obo-main/services/auth/auth.service';
import { Constants } from '@obo-app/constants';
import {
    ProjectBuildingSelectorModalComponent,
    ProjectBuildingSelectorModalUrlParameterType
} from '@obo-dashboard/components/dashboard/project/modals/project-building-selector-modal.component';
import { RegisterModalComponent } from '@obo-account/registerModal/registerModal.component';
import { mergeMap } from 'rxjs/operators';
import { ModalService } from '@obo-main/services/modal/modal.service';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import ModuleId = Constants.ModuleId;

@Injectable()
export class FinishWizardService {
    constructor(
        private planningService: PlanningService,
        private buildingService: BuildingService,
        private authService: AuthService,
        private modalService: ModalService,
        private overlay: Overlay
    ) {}

    public undoSaving(projectId: number, buildingId: number, planningId: number): Observable<any> {
        return forkJoin([
            this.planningService.deletePlanning(projectId, buildingId, planningId),
            this.buildingService.deleteBuilding(projectId, buildingId)
        ]);
    }

    public isGuest(): boolean {
        return this.authService.isInRole([Constants.Role.Guest]);
    }

    public startPreflightProcess(
        moduleId: ModuleId,
        projectId?: number,
        buildingId?: number,
        planningId?: number
    ): Observable<ProjectBuildingSelectorModalUrlParameterType> {
        let preflightObservable = of({
            projectId: projectId,
            buildingId: buildingId,
            planningId: planningId
        } as ProjectBuildingSelectorModalUrlParameterType);

        const positionStrategy = this.overlay.position().global().centerVertically().centerHorizontally();

        const overlayConfig = new OverlayConfig({
            positionStrategy,
            hasBackdrop: !projectId && !buildingId
        });

        const openPreflightRegistrationModal = () => from(this.modalService.open(RegisterModalComponent).result);

        const openPreflightProjectBuildingModal = () =>
            from(
                this.modalService.open(ProjectBuildingSelectorModalComponent, {
                    config: overlayConfig,
                    injector: Injector.create({
                        providers: [
                            {
                                provide: 'moduleId',
                                useValue: moduleId
                            },
                            {
                                provide: 'createPlanning',
                                useValue: !planningId
                            },
                            {
                                provide: 'projectId',
                                useValue: projectId
                            },
                            {
                                provide: 'buildingId',
                                useValue: buildingId
                            }
                        ]
                    })
                }).result as Promise<ProjectBuildingSelectorModalUrlParameterType>
            );

        if (this.isGuest()) {
            preflightObservable = openPreflightRegistrationModal().pipe(mergeMap(() => openPreflightProjectBuildingModal()));
        } else if (!planningId) {
            preflightObservable = openPreflightProjectBuildingModal();
        }
        return preflightObservable;
    }
}
